import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const EULA_TEXT = `ELEPHANTC2 — END USER LICENSE AGREEMENT

1. ACCESS. Your account is provided by your organization's Headquarters for business development,
   technology tracking, and team coordination. Access is personal — do not share credentials.

2. CONFIDENTIALITY. This platform contains business-sensitive information: pipeline data, customer
   relationships, internal development efforts, and personnel records. Treat everything you see here
   as company confidential. Do not export, screenshot, or share platform contents outside the
   organization without Headquarters approval.

3. PROHIBITED CONTENT. Do not enter classified information, controlled unclassified information (CUI),
   export-controlled technical data (ITAR/EAR), or another party's proprietary information into this
   platform. It is not accredited for classified or controlled material.

4. YOUR DATA. You control your profile details. Alternate contact details you provide are visible only
   to you and Headquarters. Feedback submitted to the Lead Navigator is posted anonymously if approved.

5. ACCEPTABLE USE. Use the platform professionally. Headquarters reviews Lead Navigator submissions
   before posting and may suspend accounts for misuse.

6. NO WARRANTY. The platform is provided as-is to support your work; it may change, be unavailable,
   or be updated at any time.

7. TERMINATION. Headquarters may deactivate accounts when team membership ends. Your obligations on
   confidentiality survive account termination.

By checking the box below you acknowledge that you have read and agree to these terms.

(This agreement is a working baseline — final language subject to counsel review.)`;

const TUTORIAL_SLIDES = [
  {
    title: 'The Watering Hole',
    body: 'Where the herd gathers. Your daily snapshot: pipeline pulse, actions due, the technology gap, and answers from the Lead Navigator. Once a day it greets you with the state of the herd.',
  },
  {
    title: 'The Family',
    body: 'Who we are. Your profile lives here — define your position, duties, and skills, upload your photo, and add private contact details (only you and Headquarters can see those). Track your assigned efforts and log progress as you go.',
  },
  {
    title: 'The Herd & The Savanna',
    body: 'The Herd holds our government relationships — contacts and the network connecting them. The Savanna is the terrain: the government landscape organized by statutory authority, and the supply chain behind our capabilities.',
  },
  {
    title: 'The Charge & Memory',
    body: 'The Charge is execution — the opportunity pipeline and the industry field of rivals and teammates. Memory is the reference library: glossary, acronym finder, and the twelve technology verticals.',
  },
  {
    title: 'Talk to the Lead Navigator',
    body: 'Questions, friction, ideas — submit them anytime from the Watering Hole. Feedback is reviewed and posted anonymously with answers from Headquarters, so the whole family learns together. Welcome aboard.',
  },
];

const card = {
  background: '#111827', border: '1px solid #22D3EE33', borderRadius: 12,
  width: 580, maxWidth: '94vw', maxHeight: '90vh', overflowY: 'auto',
  padding: '28px 30px', color: '#F1F5F9', boxShadow: '0 0 60px rgba(34,211,238,0.08)',
};
const btn = {
  width: '100%', padding: '11px 0', background: '#22D3EE', color: '#0A0F1C',
  border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: 13,
};
const inp = {
  width: '100%', boxSizing: 'border-box', background: '#0A0F1C', border: '1px solid #1E2D45',
  color: '#F1F5F9', padding: '10px 12px', borderRadius: 6, fontSize: 13, marginBottom: 10,
};

const InvitationPage = ({ token, onSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [invitationValid, setInvitationValid] = useState(false);
  const [step, setStep] = useState(0); // 0 = EULA, 1 = tutorial, 2 = account
  const [eulaChecked, setEulaChecked] = useState(false);
  const [slide, setSlide] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/invitation/${token}`)
      .then(res => res.json())
      .then(data => {
        if (data.valid) {
          setEmail(data.email);
          setInvitationValid(true);
        } else {
          setError(data.error || 'Invalid invitation');
        }
        setLoading(false);
      })
      .catch(() => { setError('Failed to verify invitation'); setLoading(false); });
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) { setError('Passwords do not match'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setSubmitting(true); setError('');
    try {
      const response = await fetch(`${API_URL}/api/accept-invitation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, email, password, eula_accepted: true }),
      });
      const data = await response.json();
      if (response.ok) {
        localStorage.setItem('token', data.token);
        window.history.replaceState({}, '', '/');
        onSuccess(data.token);
      } else {
        setError(data.error);
      }
    } catch (err) { setError(err.message); } finally { setSubmitting(false); }
  };

  const shell = (children) => (
    <div style={{ minHeight: '100vh', background: '#0A0F1C', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={card}>{children}</div>
    </div>
  );

  if (loading) return shell(<p style={{ color: '#94A3B8', textAlign: 'center' }}>Verifying invitation…</p>);

  if (!invitationValid) {
    return shell(
      <div style={{ textAlign: 'center' }}>
        <img src="/elephant-icon.svg" alt="" style={{ width: 48, height: 48, opacity: 0.4, marginBottom: 12 }} />
        <h2 style={{ color: '#c4a563', margin: '0 0 8px' }}>Invitation not valid</h2>
        <p style={{ color: '#94A3B8', fontSize: 13 }}>{error}</p>
        <p style={{ color: '#6B7280', fontSize: 12 }}>Ask Headquarters to send a fresh invitation.</p>
      </div>
    );
  }

  const header = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18 }}>
      <img src="/elephant-icon.svg" alt="" style={{ width: 44, height: 44 }} />
      <div>
        <div style={{ fontSize: 20, fontWeight: 700 }}>Welcome to the family.</div>
        <div style={{ fontSize: 11, color: '#6B7280', fontStyle: 'normal' }}>{email} · ElephantC2 — Toujours en avant.</div>
      </div>
      <span style={{ marginLeft: 'auto', fontSize: 10, color: '#4B5563' }}>Step {step + 1} of 3</span>
    </div>
  );

  // Step 0 — EULA
  if (step === 0) {
    return shell(
      <>
        {header}
        <div style={{ fontSize: 10, color: '#c4a563', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8 }}>The Agreement</div>
        <pre style={{
          whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 11.5, lineHeight: 1.65, color: '#94A3B8',
          background: '#0A0F1C', border: '1px solid #1E2D45', borderRadius: 8, padding: '14px 16px',
          maxHeight: 320, overflowY: 'auto', margin: '0 0 14px',
        }}>{EULA_TEXT}</pre>
        <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 12, color: '#F1F5F9', marginBottom: 14, cursor: 'pointer' }}>
          <input type="checkbox" checked={eulaChecked} onChange={e => setEulaChecked(e.target.checked)} style={{ marginTop: 2 }} />
          I have read and agree to the End User License Agreement.
        </label>
        <button onClick={() => eulaChecked && setStep(1)} disabled={!eulaChecked}
          style={{ ...btn, opacity: eulaChecked ? 1 : 0.4, cursor: eulaChecked ? 'pointer' : 'default' }}>
          Agree & Continue
        </button>
      </>
    );
  }

  // Step 1 — tutorial
  if (step === 1) {
    const s = TUTORIAL_SLIDES[slide];
    const last = slide === TUTORIAL_SLIDES.length - 1;
    return shell(
      <>
        {header}
        <div style={{ fontSize: 10, color: '#c4a563', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8 }}>
          A Quick Walk Around — {slide + 1}/{TUTORIAL_SLIDES.length}
        </div>
        <div style={{ background: '#0A0F1C', border: '1px solid #1E2D45', borderRadius: 8, padding: '22px 20px', minHeight: 150, marginBottom: 14 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#22D3EE', marginBottom: 8 }}>{s.title}</div>
          <div style={{ fontSize: 13, color: '#94A3B8', lineHeight: 1.7 }}>{s.body}</div>
        </div>
        <div style={{ display: 'flex', gap: 5, justifyContent: 'center', marginBottom: 14 }}>
          {TUTORIAL_SLIDES.map((_, i) => (
            <span key={i} onClick={() => setSlide(i)} style={{
              width: 7, height: 7, borderRadius: '50%', cursor: 'pointer',
              background: i === slide ? '#22D3EE' : '#1E2D45',
            }} />
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {slide > 0 && (
            <button onClick={() => setSlide(slide - 1)}
              style={{ ...btn, width: 110, background: 'transparent', border: '1px solid #1E2D45', color: '#94A3B8' }}>
              Back
            </button>
          )}
          <button onClick={() => (last ? setStep(2) : setSlide(slide + 1))} style={btn}>
            {last ? 'Set Up My Account' : 'Next'}
          </button>
        </div>
      </>
    );
  }

  // Step 2 — account creation
  return shell(
    <>
      {header}
      <div style={{ fontSize: 10, color: '#c4a563', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8 }}>Your Account</div>
      <form onSubmit={handleSubmit}>
        <input type="email" value={email} disabled style={{ ...inp, color: '#6B7280' }} />
        <input type="password" placeholder="Set password" value={password} onChange={e => setPassword(e.target.value)} required style={inp} />
        <input type="password" placeholder="Confirm password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required style={inp} />
        <button type="submit" disabled={submitting} style={btn}>
          {submitting ? 'Joining…' : 'Join the Family'}
        </button>
      </form>
      <div style={{ fontSize: 11, color: '#6B7280', marginTop: 12, lineHeight: 1.6 }}>
        After you join: open <strong style={{ color: '#94A3B8' }}>The Family</strong> tab and define your position —
        your duties, skills, and a photo. Headquarters has already placed you on a team.
      </div>
      {error && <p style={{ color: '#EF4444', fontSize: 12, marginTop: 10 }}>{error}</p>}
    </>
  );
};

export default InvitationPage;
